import React, { useState, useEffect, useCallback } from 'react';
import { listRepositories, searchRepos } from 'service/ncoreBoardService';
import { useAuth } from 'contexts/AuthContext';
import RepoCard from './components/RepoCard/RepoCard';
import CreateRepoModal from './components/CreateRepoModal/CreateRepoModal';
import HelpModal from './components/HelpModal/HelpModal';
import InstallModal from './components/InstallModal/InstallModal';
import styles from './NcoreHubPage.module.scss';

const SORT_OPTIONS = [
    { value: 'updated', label: 'По обновлению' },
    { value: 'stars', label: 'По звёздам' },
    { value: 'name', label: 'По имени' },
    { value: 'created', label: 'По дате создания' },
];

export default function NcoreHubPage() {
    const { user } = useAuth();
    const [repos, setRepos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [installOpen, setInstallOpen] = useState(false);
    const [filter, setFilter] = useState('all'); // all | mine | starred
    const [sort, setSort] = useState('updated');
    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => { setPage(1); }, [filter, sort, searchQuery]);
    useEffect(() => { loadRepos(); }, [filter, sort, searchQuery, page, user]);

    async function loadRepos() {
        setLoading(true);
        try {
            let data;
            if (searchQuery.trim()) {
                data = await searchRepos(searchQuery, 'repositories');
            } else {
                const params = { sort, page };
                if (filter === 'mine' && user) params.owner = user.username;
                if (filter === 'starred') params.starred = true;
                data = await listRepositories(params);
            }
            setRepos(data.results || data);
            setTotalPages(data.total_pages || Math.ceil((data.count || (data.results || data).length) / 20) || 1);
            setTotalCount(data.count || (data.results || data).length);
        } catch {
            setRepos([]);
        }
        setLoading(false);
    }

    function handleSearch(e) {
        e.preventDefault();
        setSearchQuery(search);
    }

    const subjects = filter === 'mine' ? 'Мои репозитории' : filter === 'starred' ? 'Понравившиеся' : 'Все репозитории';

    return (
        <div className={styles.page}>
            {/* Early version banner */}
            <div className={styles.earlyBanner}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 19h20L12 2zm0 3.5L19.5 17h-15L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
                </svg>
                <span>⚙️ Ранняя версия — NcoreHub активно разрабатывается. Возможны баги и незавершённые функции.</span>
            </div>

            {/* Hero header */}
            <div className={styles.hero}>
                <div className={styles.heroInner}>
                    <div className={styles.heroText}>
                        <div className={styles.heroIcon}>
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
                                <g transform="translate(12,12)">
                                    <path d="M0-2Q2-5,0-8Q-2-5,0-2Z"/>
                                    <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(72)"/>
                                    <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(144)"/>
                                    <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(216)"/>
                                    <path d="M0-2Q2-5,0-8Q-2-5,0-2Z" transform="rotate(288)"/>
                                    <circle cx="0" cy="0" r="2"/>
                                    <path d="M0 2Q-0.8 5,-0.8 8L0.8 8Q0.8 5,0 2Z"/>
                                </g>
                            </svg>
                        </div>
                        <div>
                            <h1 className={styles.heroTitle}>NcoreHub</h1>
                            <p className={styles.heroSub}>Управляй своими репозиториями. Публичные репозитории доступны всем.</p>
                        </div>
                    </div>
                    <div className={styles.heroActions}>
                        <button className={styles.installBtn} onClick={() => setInstallOpen(true)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            Скачать ncore CLI
                        </button>
                    {user && (
                        <button className={styles.createBtn} onClick={() => setCreateOpen(true)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                            Новый репозиторий
                        </button>
                    )}
                    </div>
                </div>
            </div>

            <div className={styles.main}>
                {/* Filters & search */}
                <div className={styles.toolbar}>
                    <div className={styles.searchBarRow}>
                    <form className={styles.searchForm} onSubmit={handleSearch}>
                        <div className={styles.searchWrap}>
                            <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                            </svg>
                            <input
                                className={styles.searchInput}
                                placeholder="Поиск репозиториев..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <button type="submit" className={styles.searchBtn} hidden />
                    </form>
                    <button className={styles.helpBtn} type="button" onClick={() => setHelpOpen(true)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>
                        Инструкция
                    </button>
                    </div>

                    <div className={styles.filterRow}>
                        {[
                            { value: 'all', label: 'Все' },
                            ...(user ? [{ value: 'mine', label: 'Мои' }] : []),
                            ...(user ? [{ value: 'starred', label: '⭐ Избранные' }] : []),
                        ].map(f => (
                            <button
                                key={f.value}
                                className={filter === f.value ? styles.filterActive : styles.filterBtn}
                                onClick={() => setFilter(f.value)}
                            >
                                {f.label}
                            </button>
                        ))}
                        <div className={styles.sortWrap}>
                            <select
                                className={styles.sortSelect}
                                value={sort}
                                onChange={e => setSort(e.target.value)}
                            >
                                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results header */}
                <div className={styles.resultsHeader}>
                    <span className={styles.resultsCount}>
                        {searchQuery ? `Результаты поиска «${searchQuery}»` : subjects}
                        {!loading && <span className={styles.countBadge}>{totalCount}</span>}
                    </span>
                    {searchQuery && (
                        <button className={styles.clearSearch} onClick={() => { setSearch(''); setSearchQuery(''); }}>
                            Очистить
                        </button>
                    )}
                </div>

                {/* Repo grid */}
                {loading ? (
                    <div className={styles.loadingGrid}>
                        {[1,2,3,4,5,6].map(i => <div key={i} className={styles.skeleton} />)}
                    </div>
                ) : repos.length === 0 ? (
                    <div className={styles.empty}>
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(255,255,255,0.2)' }}>
                            <path d="M3 3h18v18H3z" rx="2"/>
                            <path d="M3 9h18M9 21V9"/>
                        </svg>
                        <p>Репозитории не найдены</p>
                        {user && filter === 'mine' && (
                            <button className={styles.createBtn} onClick={() => setCreateOpen(true)}>
                                Создать первый репозиторий
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={styles.grid}>
                        {repos.map(repo => (
                            <RepoCard key={repo.id} repo={repo} />
                        ))}
                    </div>                )}

                {/* Pagination */}
                {totalPages > 1 && !loading && (
                    <div className={styles.pagination}>
                        <button className={styles.pageBtn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                            ← Назад
                        </button>
                        <span className={styles.pageInfo}>Страница {page} из {totalPages}</span>
                        <button className={styles.pageBtn} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                            Вперёд →
                        </button>
                    </div>
                )}
            </div>

            {createOpen && <CreateRepoModal onClose={() => setCreateOpen(false)} />}
            {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
            {installOpen && <InstallModal onClose={() => setInstallOpen(false)} />}
        </div>
    );
}
